import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Package, Wallet, TrendingUp, Clock, Plus, Truck, Upload,
  IndianRupee, Loader2, MapPin, Box,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_BADGE = {
  NETWORK: "bg-blue-100 text-blue-700 border-blue-200",
  REVENUE: "bg-purple-100 text-purple-700 border-purple-200",
  EXECUTION: "bg-emerald-100 text-emerald-700 border-emerald-200",
};

function fmt(n) {
  return `\u20B9${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function PartnerDashboard() {
  const { user } = useAuth();
  const token = localStorage.getItem("sendtoo_token");
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const partnerId = user?.partner_id || user?.id;
  const partnerType = user?.partner_type || "NETWORK";

  const [stats, setStats] = useState({
    today_count: 0,
    week_earnings: 0,
    pending_payout: 0,
    wallet_balance: 0,
  });
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pickupNotifs, setPickupNotifs] = useState([]);
  const [incomingNotifs, setIncomingNotifs] = useState([]);

  const fetchData = useCallback(async () => {
    if (!partnerId) return;
    setLoading(true);
    try {
      // Ledger
      const ledgerRes = await fetch(`${API}/api/v2/partners/${partnerId}/ledger`, { headers });
      const ledger = ledgerRes.ok ? await ledgerRes.json() : [];
      const entries = Array.isArray(ledger) ? ledger : (ledger.items || []);

      // Wallet
      const walletRes = await fetch(`${API}/api/v2/partners/${partnerId}/wallet`, { headers });
      const wallet = walletRes.ok ? await walletRes.json() : {};

      // Calculate stats from ledger
      const today = new Date().toISOString().slice(0, 10);
      const weekAgo = new Date(Date.now() - 7 * 24 * 3600 * 1000).toISOString();
      const todayCount = entries.filter((e) =>
        (e.created_at || "").startsWith(today)
      ).length;
      const weekEarnings = entries
        .filter((e) => (e.created_at || "") >= weekAgo)
        .reduce((s, e) => s + (e.commission_amount || e.amount || 0), 0);
      const pending = entries
        .filter((e) => (e.payout_status || e.status) === "PENDING")
        .reduce((s, e) => s + (e.commission_amount || e.amount || 0), 0);

      setStats({
        today_count: todayCount,
        week_earnings: weekEarnings,
        pending_payout: pending,
        wallet_balance: wallet.balance || 0,
      });
      setRecent(entries.slice(0, 10));
    } catch (e) {
      // silent
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Fetch partner notifications (best-effort — endpoint may return empty)
  useEffect(() => {
    if (!partnerId) return;
    (async () => {
      try {
        const r1 = await fetch(`${API}/api/v2/partner-notifications/${partnerId}?type=PICKUP_REQUEST`, { headers });
        if (r1.ok) setPickupNotifs(await r1.json());
      } catch { /* ignore — endpoint optional */ }
      try {
        const r2 = await fetch(`${API}/api/v2/partner-notifications/${partnerId}?type=INCOMING_DELIVERY`, { headers });
        if (r2.ok) setIncomingNotifs(await r2.json());
      } catch { /* ignore */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerId]);

  const canBook = partnerType === "NETWORK" || partnerType === "REVENUE";
  const canUpload = partnerType === "NETWORK" || partnerType === "EXECUTION";

  return (
    <DashboardLayout portalColor="green">
      <div className="space-y-5" data-testid="partner-dashboard">
        {/* HEADER */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Partner Dashboard</h1>
            <p className="text-sm text-slate-500">Welcome back, {user?.name || "Partner"}</p>
          </div>
          <Badge className={`text-xs font-bold uppercase tracking-wider px-3 py-1.5 border ${TYPE_BADGE[partnerType] || TYPE_BADGE.NETWORK}`}
                 data-testid="partner-type-badge">
            {partnerType} Partner
          </Badge>
        </div>

        {/* SUMMARY CARDS */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard
            icon={Package}
            label="Today's Shipments"
            value={stats.today_count}
            color="text-blue-600"
            testid="stat-today"
          />
          <StatCard
            icon={TrendingUp}
            label="This Week Earnings"
            value={fmt(stats.week_earnings)}
            color="text-emerald-600"
            testid="stat-week"
          />
          <StatCard
            icon={Clock}
            label="Pending Payout"
            value={fmt(stats.pending_payout)}
            color="text-amber-600"
            testid="stat-pending"
          />
          <StatCard
            icon={Wallet}
            label="Wallet Balance"
            value={fmt(stats.wallet_balance)}
            color={stats.wallet_balance >= 0 ? "text-emerald-600" : "text-red-600"}
            testid="stat-wallet"
          />
        </div>

        {/* QUICK ACTIONS */}
        <div>
          <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-2">Quick Actions</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {canBook && (
              <Link to="/partner/bookings" data-testid="action-new-booking">
                <ActionCard icon={Plus} label="New Booking" color="bg-emerald-50 text-emerald-700 border-emerald-200" />
              </Link>
            )}
            <Link to="/partner/shipments" data-testid="action-shipments">
              <ActionCard icon={Truck} label="My Shipments" color="bg-blue-50 text-blue-700 border-blue-200" />
            </Link>
            <Link to="/partner/earnings" data-testid="action-earnings">
              <ActionCard icon={IndianRupee} label="My Earnings" color="bg-amber-50 text-amber-700 border-amber-200" />
            </Link>
            {canUpload && (
              <Link to="/partner/upload-proof" data-testid="action-proof">
                <ActionCard icon={Upload} label="Upload Proof" color="bg-purple-50 text-purple-700 border-purple-200" />
              </Link>
            )}
          </div>
        </div>

        {/* RECENT ACTIVITY */}
        <div className="bg-white border rounded-lg overflow-hidden" data-testid="recent-activity">
          <div className="px-4 py-3 border-b bg-slate-50 flex items-center justify-between">
            <h2 className="text-sm font-bold text-slate-700">Recent Activity</h2>
            <Link to="/partner/earnings" className="text-xs text-emerald-600 hover:underline">View all →</Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-4 py-2 font-semibold">Date</th>
                  <th className="text-left px-4 py-2 font-semibold">AWB / Shipment</th>
                  <th className="text-left px-4 py-2 font-semibold">Action</th>
                  <th className="text-right px-4 py-2 font-semibold">Commission</th>
                  <th className="text-left px-4 py-2 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={5} className="py-8 text-center">
                    <Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" />
                  </td></tr>
                ) : recent.length === 0 ? (
                  <tr><td colSpan={5} className="py-8 text-center text-slate-400">No recent activity</td></tr>
                ) : (
                  recent.map((r, i) => (
                    <tr key={r.id || r._id || i} className="border-b border-slate-100" data-testid={`recent-row-${i}`}>
                      <td className="px-4 py-2 text-slate-500">
                        {r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}
                      </td>
                      <td className="px-4 py-2 font-mono text-[11px]">{r.awb || r.shipment_id || "—"}</td>
                      <td className="px-4 py-2">{r.action || r.txn_type || r.description || "—"}</td>
                      <td className="px-4 py-2 text-right font-medium text-emerald-600">
                        {fmt(r.commission_amount || r.amount || 0)}
                      </td>
                      <td className="px-4 py-2">
                        <StatusBadge status={r.payout_status || r.status || "PENDING"} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* SECTION A — PICKUP QUEUE */}
        <div className="bg-white border rounded-lg overflow-hidden" data-testid="partner-pickup-queue">
          <div className="px-4 py-3 border-b bg-slate-50">
            <h2 className="text-sm font-bold text-slate-700 flex items-center gap-1.5">
              <Box className="w-4 h-4" /> Pickup Requests Assigned to You
            </h2>
          </div>
          <div className="p-4">
            {pickupNotifs.length === 0 ? (
              <p className="text-xs text-slate-400">No pickup requests assigned</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {pickupNotifs.map((n, i) => (
                  <div key={i} className="border rounded p-3" data-testid={`pickup-notif-${i}`}>
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-mono text-xs">{n.pickup_id || n.id}</div>
                        <div className="text-xs text-slate-600 mt-0.5">{n.location_name || n.location || "—"}</div>
                        <div className="text-xs text-slate-500">
                          {n.pickup_date} · {n.pickup_slot || n.slot_time} · {n.box_count || 0} boxes
                        </div>
                      </div>
                    </div>
                    <div className="mt-2 flex gap-2">
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700"
                              data-testid={`accept-${i}`}>Accept</Button>
                      <Button size="sm" variant="outline"
                              data-testid={`decline-${i}`}
                              className="text-red-600 border-red-200">Decline</Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* SECTION B — INCOMING DELIVERIES */}
        <div className="bg-white border rounded-lg overflow-hidden" data-testid="partner-incoming">
          <div className="px-4 py-3 border-b bg-slate-50">
            <h2 className="text-sm font-bold text-slate-700 flex items-center gap-1.5">
              <Truck className="w-4 h-4" /> Incoming to Your Area
            </h2>
          </div>
          <div className="p-4">
            {incomingNotifs.length === 0 ? (
              <p className="text-xs text-slate-400">No incoming deliveries</p>
            ) : (
              <>
                <p className="text-[11px] text-slate-500 italic mb-2">
                  These consignments are in transit to your area (Own Network only — 3PL carriers handle their own last mile)
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {incomingNotifs.map((n, i) => (
                    <div key={i} className="border rounded p-3" data-testid={`incoming-${i}`}>
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-mono text-xs">{n.awb || n.tracking_id}</div>
                          <div className="text-xs text-slate-600 mt-0.5 flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> {n.origin_city || "—"}
                          </div>
                          <div className="text-xs text-slate-500">
                            {n.weight || n.chargeable_weight || 0} kg · {n.boxes || 1} boxes · ETA: {n.expected_date || "—"}
                          </div>
                        </div>
                        <Badge className="text-[10px] bg-blue-100 text-blue-700">INCOMING</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

function StatCard({ icon: Icon, label, value, color, testid }) {
  return (
    <div className="bg-white border rounded-lg p-4" data-testid={testid}>
      <div className="text-xs text-slate-500 flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className={`text-2xl font-bold mt-1 ${color}`}>{value}</div>
    </div>
  );
}

function ActionCard({ icon: Icon, label, color }) {
  return (
    <div className={`border rounded-lg p-4 flex items-center gap-3 hover:shadow-md transition-shadow cursor-pointer ${color}`}>
      <Icon className="w-5 h-5" />
      <span className="font-semibold text-sm">{label}</span>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    PENDING: "bg-amber-100 text-amber-700",
    PAID: "bg-emerald-100 text-emerald-700",
    DISPUTED: "bg-red-100 text-red-700",
    HELD: "bg-orange-100 text-orange-700",
    SETTLED: "bg-emerald-100 text-emerald-700",
  };
  const cls = map[(status || "").toUpperCase()] || "bg-slate-100 text-slate-600";
  return <Badge className={`text-[10px] ${cls}`}>{status || "—"}</Badge>;
}
